import { useCallback, useEffect, useState, type ReactNode } from "react";
import { api, loadStoredToken, setAuthToken } from "../api/client";
import type { AuthResponse, User } from "../types/api";
import { AuthContext } from "./authContextInstance";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      const token = loadStoredToken();
      if (!token) {
        return;
      }
      try {
        const me = await api.auth.me();
        if (!cancelled) {
          setUser(me);
        }
      } catch {
        setAuthToken(null);
      }
    }

    restoreSession().finally(() => {
      if (!cancelled) {
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (identifier: string, password: string) => {
    const result = await api.auth.login(identifier, password);
    setAuthToken(result.access_token);
    setUser(result.user);
  }, []);

  const register = useCallback(async (email: string, password: string) => {
    const result = await api.auth.register(email, password);
    setAuthToken(result.access_token);
    setUser(result.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } finally {
      setAuthToken(null);
      setUser(null);
    }
  }, []);

  const applySession = useCallback((result: AuthResponse) => {
    setAuthToken(result.access_token);
    setUser(result.user);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setUser, applySession }}>
      {children}
    </AuthContext.Provider>
  );
}
